import React, { type ReactNode } from 'react';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import {
  CheckCircle as SuccessIcon,
  Description as DefaultIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { getWorkflowWorkspacePalette, type WorkspaceTone } from './WorkflowWorkspacePrimitives';

export interface WorkspaceEmptyStateProps {
  title: ReactNode;
  description?: ReactNode;
  steps?: string[];
  actionLabel?: string;
  onAction?: () => void;
  tone?: WorkspaceTone;
  dataTestId?: string;
}

const ICON_SIZE = 48;
const ICON_CIRCLE_SIZE = 96;

const toneMeta: Record<
  WorkspaceTone,
  { icon: typeof DefaultIcon }
> = {
  neutral: { icon: DefaultIcon },
  accent: { icon: DefaultIcon },
  success: { icon: SuccessIcon },
  warning: { icon: WarningIcon },
  error: { icon: ErrorIcon },
  info: { icon: InfoIcon },
};

const getToneColor = (theme: Theme, tone: WorkspaceTone): string => {
  switch (tone) {
    case 'accent':
      return theme.palette.primary.main;
    case 'success':
      return theme.palette.success.main;
    case 'warning':
      return theme.palette.warning.main;
    case 'error':
      return theme.palette.error.main;
    case 'info':
      return theme.palette.info.main;
    default:
      return theme.palette.text.secondary;
  }
};

export default function WorkspaceEmptyState({
  title,
  description,
  steps,
  actionLabel,
  onAction,
  tone = 'neutral',
  dataTestId,
}: WorkspaceEmptyStateProps) {
  const theme = useTheme();
  const palette = getWorkflowWorkspacePalette(theme);
  const toneColor = getToneColor(theme, tone);
  const Icon = toneMeta[tone].icon;

  return (
    <Box
      data-testid={dataTestId}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        minHeight: 400,
        py: 6,
        px: 3,
        gap: 2,
      }}
    >
      <Box
        sx={{
          width: ICON_CIRCLE_SIZE,
          height: ICON_CIRCLE_SIZE,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: alpha(toneColor, 0.08),
          mb: 0.5,
        }}
      >
        <Icon sx={{ fontSize: ICON_SIZE, color: toneColor, opacity: 0.9 }} />
      </Box>

      <Typography
        variant="h5"
        sx={{
          fontWeight: 600,
          color: palette.headingText,
          lineHeight: 1.25,
        }}
      >
        {title}
      </Typography>

      {description ? (
        <Typography
          variant="body1"
          sx={{
            color: palette.bodyText,
            maxWidth: 420,
            lineHeight: 1.6,
          }}
        >
          {description}
        </Typography>
      ) : null}

      {steps && steps.length > 0 ? (
        <Stack
          component="ol"
          spacing={layoutGap.dense}
          alignItems="flex-start"
          sx={{
            mt: 0.5,
            pl: 2,
            textAlign: 'left',
            color: palette.bodyText,
            maxWidth: 420,
            '& li::marker': {
              color: palette.tertiaryText,
              fontWeight: 700,
            },
          }}
        >
          {steps.map((step, index) => (
            <Typography key={index} component="li" variant="body1" sx={{ lineHeight: 1.6 }}>
              {step}
            </Typography>
          ))}
        </Stack>
      ) : null}

      {actionLabel && onAction ? (
        <Button
          variant="contained"
          size="large"
          onClick={onAction}
          sx={{ mt: 1 }}
        >
          {actionLabel}
        </Button>
      ) : null}
    </Box>
  );
}
